import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Building2, ChevronLeft, ChevronRight, FolderOpen, LayoutDashboard, MessageCircle, Newspaper, Search, User, Users,
} from "lucide-react";
import { FaTelegramPlane } from "react-icons/fa";
import { vociMenuApi, type VociMenuAgenzia } from "@/api/vociMenu";
import { Interruttore } from "@/components/Interruttore";
import { cn } from "@/lib/utils";
import type { VoceMenu } from "@/types";

// Le sei voci nell'ordine del menù, con le icone del menù vero (Sidebar.tsx).
const VOCI: { k: VoceMenu; label: string; icon: React.ReactNode }[] = [
  { k: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={17} /> },
  { k: "preventivo", label: "Preventivo condomini", icon: <Building2 size={17} /> },
  { k: "consulenza", label: "Consulenza", icon: <MessageCircle size={17} /> },
  { k: "pratiche", label: "Pratiche", icon: <FolderOpen size={17} /> },
  { k: "clienti", label: "Clienti", icon: <Users size={17} /> },
  { k: "telegram", label: "Telegram", icon: <FaTelegramPlane size={17} /> },
];
const SEMPRE = [
  { label: "Novità", icon: <Newspaper size={17} /> },
  { label: "Profilo", icon: <User size={17} /> },
];

/** Il riassunto conta solo le voci accese di partenza: il Preventivo è spento quasi ovunque. */
function riassunto(a: VociMenuAgenzia): string {
  const n = VOCI.filter((v) => v.k !== "preventivo" && !a.voci[v.k]).length;
  if (n === 0) return "Menù completo";
  return n === 1 ? "1 voce spenta" : `${n} voci spente`;
}

const piano = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

type Toast = { msg: string; type: "ok" | "err" };

/**
 * Console amministratore «Voci del menù» (spec 2026-10-31, bozza B): a sinistra le agenzie, a destra
 * le voci dell'agenzia scelta con l'anteprima del menù che vedranno. Sul telefono due schermate,
 * l'elenco e l'agenzia aperta; l'agenzia scelta sta nell'indirizzo. Ogni interruttore salva subito ed
 * è ottimistico, come in QrAgenzie.tsx: se il salvataggio non riesce torna com'era e compare l'avviso.
 */
export function VociMenu() {
  const [agenzie, setAgenzie] = useState<VociMenuAgenzia[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [cerca, setCerca] = useState("");
  const [toast, setToast] = useState<Toast | null>(null);
  const [params, setParams] = useSearchParams();
  const scelta = params.get("agenzia");

  function showToast(msg: string, type: Toast["type"]) {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  useEffect(() => {
    vociMenuApi
      .lista()
      .then((r) => setAgenzie(r.agenzie))
      .catch(() => showToast("Errore aggiornamento stato", "err"))
      .finally(() => setCaricamento(false));
  }, []);

  const filtrate = useMemo(
    () => agenzie.filter((a) => piano(a.nome ?? a.account_id).includes(piano(cerca.trim()))),
    [agenzie, cerca],
  );
  const aperta = agenzie.find((a) => a.account_id === scelta) ?? agenzie[0];

  async function cambia(a: VociMenuAgenzia, voce: VoceMenu) {
    const nuovo = !a.voci[voce];
    const metti = (valore: boolean) =>
      setAgenzie((prev) => prev.map((x) =>
        x.account_id === a.account_id ? { ...x, voci: { ...x.voci, [voce]: valore } } : x));
    metti(nuovo);
    try {
      await vociMenuApi.imposta(a.account_id, voce, nuovo);
    } catch {
      metti(!nuovo);
      showToast("Errore aggiornamento stato", "err");
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Voci del menù</h2>
        <p className="text-sm text-muted-foreground">
          Profilo, Novità, «Segnala un problema» ed «Esci» restano sempre visibili.
        </p>
      </div>

      {caricamento ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : (
        <div className="md:flex md:items-start md:gap-4">
          <div className={cn("space-y-2 md:w-[280px] md:shrink-0", scelta && "hidden md:block")}>
            <label className="relative block">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                aria-label="Cerca agenzia"
                placeholder="Cerca agenzia"
                value={cerca}
                onChange={(e) => setCerca(e.target.value)}
                className="h-11 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-base md:text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              />
            </label>
            {/* Senza ricerca un elenco vuoto è un caricamento non riuscito (l'avviso lo dice), non
                una ricerca andata a vuoto. */}
            {filtrate.length === 0 ? (
              cerca.trim() && <p className="text-sm text-muted-foreground px-1 py-2">Nessuna agenzia con questo nome.</p>
            ) : (
              <ul aria-label="Agenzie" className="overflow-hidden rounded-lg border border-border bg-card">
                {filtrate.map((a) => (
                  <li key={a.account_id} className="border-b border-border last:border-0">
                    <button
                      type="button"
                      onClick={() => setParams({ agenzia: a.account_id })}
                      aria-current={aperta?.account_id === a.account_id ? "true" : undefined}
                      className={cn(
                        "flex min-h-[56px] w-full items-center gap-3 px-4 py-2 text-left transition-colors hover:bg-muted",
                        aperta?.account_id === a.account_id && "md:bg-primary/10",
                      )}
                    >
                      <span className="flex-1">
                        <span className="block text-[15px] font-medium text-foreground">{a.nome ?? a.account_id}</span>
                        <span className="block text-xs text-muted-foreground">{riassunto(a)}</span>
                      </span>
                      <ChevronRight size={16} className="text-muted-foreground md:hidden" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {aperta && (
            <div className={cn("flex-1 space-y-2", !scelta && "hidden md:block")}>
              <button
                type="button"
                onClick={() => setParams({})}
                className="flex min-h-[44px] items-center gap-1 text-sm font-medium text-primary md:hidden"
              >
                <ChevronLeft size={16} />
                Agenzie
              </button>
              <h3 className="font-semibold text-foreground">{aperta.nome ?? aperta.account_id}</h3>
              <div className="flex flex-col gap-4 lg:flex-row">
                <ul className="flex-1 rounded-lg border border-border bg-card">
                  {VOCI.map((v) => (
                    <li key={v.k} className="flex min-h-[52px] items-center gap-3 border-b border-border px-4 last:border-0">
                      <span className="text-muted-foreground">{v.icon}</span>
                      <span className={cn("flex-1 text-[15px]", aperta.voci[v.k] ? "text-foreground" : "text-muted-foreground")}>
                        {v.label}
                      </span>
                      <Interruttore
                        acceso={aperta.voci[v.k]}
                        onClick={() => void cambia(aperta, v.k)}
                        title={aperta.voci[v.k] ? "Disattiva" : "Attiva"}
                        ariaLabel={`${v.label} – ${aperta.nome ?? aperta.account_id}`}
                      />
                    </li>
                  ))}
                </ul>
                <section aria-label="Anteprima del menù" className="space-y-1 lg:w-[200px] lg:shrink-0">
                  <div className="text-xs text-muted-foreground">Anteprima del menù</div>
                  <div className="w-[200px] rounded-lg bg-sidebar p-2">
                    <ul>
                      {VOCI.filter((v) => aperta.voci[v.k]).map((v) => (
                        <li key={v.k} className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-[#a9c2ab]">
                          <span className="text-[#7fa384]">{v.icon}</span>
                          {v.label}
                        </li>
                      ))}
                    </ul>
                    {VOCI.some((v) => aperta.voci[v.k]) && <hr className="mx-3 my-2 border-white/10" />}
                    <ul>
                      {SEMPRE.map((s) => (
                        <li key={s.label} className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-[#a9c2ab]">
                          <span className="text-[#7fa384]">{s.icon}</span>
                          {s.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>
              </div>
            </div>
          )}
        </div>
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-sm shadow-lg border ${
            toast.type === "ok"
              ? "bg-green-100 text-green-800 border-green-200"
              : "bg-red-100 text-red-800 border-red-200"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
